import React, { useState, useEffect } from 'react';
import { aiService } from '../services/aiService';

interface McpTool {
  name: string;
  description: string;
  inputSchema?: any;
  server?: string;
  category?: string;
}

interface ToolExecution {
  id: string;
  toolName: string;
  parameters: Record<string, any>;
  status: 'pending' | 'running' | 'completed' | 'failed';
  result?: any;
  error?: string;
  timestamp: string;
  executionTime?: number;
  userId?: string;
  server?: string;
}

interface McpToolsPanelProps {
  onClose: () => void;
  addLog: (level: 'INFO' | 'WARN' | 'ERROR' | 'AI_ACTION', message: string, component?: string) => void;
}

const McpToolsPanel: React.FC<McpToolsPanelProps> = ({ onClose, addLog }) => {
  const [tools, setTools] = useState<McpTool[]>([]);
  const [loading, setLoading] = useState(true);
  const [executions, setExecutions] = useState<ToolExecution[]>([]);
  const [selectedTool, setSelectedTool] = useState<McpTool | null>(null);
  const [toolParameters, setToolParameters] = useState<Record<string, any>>({});
  const [isExecuting, setIsExecuting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [serverHealth, setServerHealth] = useState<any>(null);
  const [auditLogs, setAuditLogs] = useState<any[]>([]);
  const [statistics, setStatistics] = useState<any>(null);
  const [activeTab, setActiveTab] = useState<'tools' | 'audit' | 'stats'>('tools');

  // Load available MCP tools and initialize monitoring
  useEffect(() => {
    loadTools();
    loadServerHealth();
    loadAuditLogs();
    loadStatistics();
    
    // Set up periodic monitoring
    const interval = setInterval(() => {
      if (activeTab === 'audit') {
        loadAuditLogs();
      }
      if (activeTab === 'stats') {
        loadStatistics();
      }
    }, 10000); // Update every 10 seconds
    
    return () => clearInterval(interval);
  }, [activeTab]);

  const loadTools = async () => {
    try {
      setLoading(true);
      const availableTools = await aiService.getMCPTools();
      
      // Enhanced tool definitions with proper schemas
      const defaultTools: McpTool[] = [
        {
          name: 'system_info',
          description: 'Get comprehensive system information including hardware specs, OS details, and resource usage',
          category: 'System',
          server: 'desktop-commander',
          inputSchema: {
            type: 'object',
            properties: {
              detail_level: {
                type: 'string',
                enum: ['basic', 'detailed', 'full'],
                default: 'basic',
                description: 'Level of detail for system information'
              }
            }
          }
        },
        {
          name: 'file_operations',
          description: 'Perform file system operations like copy, move, delete, and search',
          category: 'File System',
          server: 'desktop-commander',
          inputSchema: {
            type: 'object',
            properties: {
              operation: {
                type: 'string',
                enum: ['read', 'write', 'list', 'search'],
                description: 'File operation to perform'
              },
              path: {
                type: 'string',
                description: 'File or directory path'
              },
              content: {
                type: 'string',
                description: 'Content to write (for write operation)'
              },
              pattern: {
                type: 'string',
                description: 'Search pattern (for search operations)'
              }
            },
            required: ['operation', 'path']
          }
        },
        {
          name: 'process_manager',
          description: 'Manage system processes - list, kill, monitor resource usage',
          category: 'Process',
          server: 'desktop-commander',
          inputSchema: {
            type: 'object',
            properties: {
              action: {
                type: 'string',
                enum: ['list', 'kill', 'monitor'],
                description: 'Process management action'
              },
              pid: {
                type: 'integer',
                description: 'Process ID (required for kill action)'
              },
              name: {
                type: 'string',
                description: 'Process name pattern (for list/search)'
              }
            },
            required: ['action']
          }
        },
        {
          name: 'network_tools',
          description: 'Network diagnostics and utilities including ping, port scanning, and connectivity tests',
          category: 'Network',
          server: 'desktop-commander',
          inputSchema: {
            type: 'object',
            properties: {
              action: {
                type: 'string',
                enum: ['ping', 'scan_ports'],
                description: 'Network diagnostic action'
              },
              target: {
                type: 'string',
                description: 'Target IP/hostname'
              },
              count: {
                type: 'integer',
                description: 'Number of ping packets',
                default: 4
              },
              port_range: {
                type: 'string',
                description: 'Port range for scanning (e.g., "80-100")',
                default: '80-100'
              }
            },
            required: ['action', 'target']
          }
        },
        {
          name: 'docker_manager',
          description: 'Docker container management - list, start, stop, and inspect containers',
          category: 'Containerization',
          server: 'desktop-commander',
          inputSchema: {
            type: 'object',
            properties: {
              action: {
                type: 'string',
                enum: ['list', 'start', 'stop', 'inspect'],
                description: 'Docker container action'
              },
              container_id: {
                type: 'string',
                description: 'Container ID or name'
              },
              all: {
                type: 'boolean',
                description: 'Show all containers (for list action)',
                default: false
              }
            },
            required: ['action']
          }
        }
      ];
      
      const allTools = [...availableTools, ...defaultTools];
      setTools(allTools);
      addLog('INFO', `Loaded ${allTools.length} MCP tools`, 'MCP Tools');
    } catch (error) {
      console.error('Error loading MCP tools:', error);
      addLog('ERROR', 'Failed to load MCP tools: ' + (error as Error).message, 'MCP Tools');
    } finally {
      setLoading(false);
    }
  };
  
  const loadServerHealth = async () => {
    try {
      const health = await aiService.getMCPServerHealth();
      setServerHealth(health);
    } catch (error) {
      console.error('Error loading server health:', error);
    }
  };
  
  const loadAuditLogs = async () => {
    try {
      const logs = await aiService.getMCPAuditLogs({ count: 20 });
      setAuditLogs(logs.logs || []);
    } catch (error) {
      console.error('Error loading audit logs:', error);
    }
  };
  
  const loadStatistics = async () => {
    try {
      const stats = await aiService.getMCPStatistics();
      setStatistics(stats.statistics || {});
    } catch (error) {
      console.error('Error loading statistics:', error);
    }
  };

  const executeTool = async () => {
    if (!selectedTool) return;

    // Validate required parameters
    const missingParams = selectedTool.inputSchema?.required?.filter(
      (param: string) => !toolParameters[param] && toolParameters[param] !== 0 && toolParameters[param] !== false
    ) || [];
    
    if (missingParams.length > 0) {
      addLog('ERROR', `Missing required parameters: ${missingParams.join(', ')}`, 'MCP Tools');
      return;
    }

    setIsExecuting(true);
    const executionId = Date.now().toString();
    
    // Add to executions list
    const newExecution: ToolExecution = {
      id: executionId,
      toolName: selectedTool.name,
      parameters: { ...toolParameters }, // Clone to preserve values
      status: 'running',
      timestamp: new Date().toLocaleTimeString(),
      userId: 'dashboard-user',
      server: selectedTool.server || 'desktop-commander'
    };
    
    setExecutions(prev => [newExecution, ...prev]);

    try {
      addLog('INFO', `Executing MCP tool: ${selectedTool.name}`, 'MCP Tools');
      
      // Show progress indicator
      setExecutions(prev => prev.map(exec => 
        exec.id === executionId 
          ? { ...exec, result: 'Initializing tool execution...' } 
          : exec
      ));
      
      const result = await aiService.executeMCPTool(selectedTool.name, toolParameters, 'dashboard-user');
      
      // Process result with enhanced error handling
      let processedResult = result;
      let hasError = false;
      
      if (result && typeof result === 'object') {
        const resultObj = result as Record<string, any>;
        if (resultObj.error) {
          hasError = true;
          processedResult = resultObj.error;
        } else if (resultObj.response) {
          processedResult = resultObj.response;
        } else if (resultObj.result) {
          processedResult = resultObj.result;
        } else {
          processedResult = JSON.stringify(result, null, 2);
        }
      } else if (typeof result === 'string') {
        if (result.includes('Error:') || result.includes('error') || result.includes('Exception')) {
          hasError = true;
          processedResult = result.replace(/^Error:?\s*/i, '').trim();
        } else {
          processedResult = result;
        }
      } else if (result === null || result === undefined) {
        hasError = true;
        processedResult = 'Tool returned empty response';
      } else {
        processedResult = String(result);
      }
      
      // Enhanced result processing for different data types
      if (typeof processedResult === 'object') {
        processedResult = JSON.stringify(processedResult, null, 2);
      }
      
      // Calculate execution time
      const endTime = Date.now();
      const executionTime = (endTime - parseInt(executionId)) / 1000;
      
      // Update execution status
      setExecutions(prev => prev.map(exec => 
        exec.id === executionId 
          ? { 
              ...exec, 
              status: hasError ? 'failed' : 'completed', 
              result: processedResult,
              error: hasError ? processedResult : undefined,
              executionTime: executionTime
            } 
          : exec
      ));
      
      if (hasError) {
        addLog('ERROR', `MCP tool execution failed: ${selectedTool.name} - ${processedResult}`, 'MCP Tools');
      } else {
        addLog('AI_ACTION', `MCP tool execution completed: ${selectedTool.name}`, 'MCP Tools');
      }
    } catch (error) {
      console.error('Error executing MCP tool:', error);
      const errorMessage = (error as Error).message || 'Unknown execution error';
      
      // Update execution status with error
      setExecutions(prev => prev.map(exec => 
        exec.id === executionId 
          ? { ...exec, status: 'failed', error: errorMessage, result: `Execution failed: ${errorMessage}` } 
          : exec
      ));
      
      addLog('ERROR', `MCP tool execution failed: ${selectedTool.name} - ${errorMessage}`, 'MCP Tools');
    } finally {
      setIsExecuting(false);
    }
  };

  const filteredTools = tools.filter(tool => 
    tool.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    tool.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (tool.category && tool.category.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const groupedTools = filteredTools.reduce((acc, tool) => {
    const category = tool.category || 'Uncategorized';
    if (!acc[category]) acc[category] = [];
    acc[category].push(tool);
    return acc;
  }, {} as Record<string, McpTool[]>);

  const renderParameterInput = (paramName: string, paramSchema: any) => {
    const value = toolParameters[paramName] || '';
    
    const handleChange = (newValue: any) => {
      setToolParameters(prev => ({
        ...prev,
        [paramName]: newValue
      }));
    };

    // Handle different parameter types
    switch (paramSchema.type) {
      case 'string':
        if (paramSchema.enum) {
          return (
            <select
              value={value}
              onChange={(e) => handleChange(e.target.value)}
              className="w-full bg-[#1e222d] border border-[#2a2e39] rounded px-3 py-2 text-slate-300 text-[11px] focus:outline-none focus:border-purple-500"
            >
              {paramSchema.enum.map((option: string) => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          );
        }
        return (
          <input
            type="text"
            value={value}
            onChange={(e) => handleChange(e.target.value)}
            placeholder={paramSchema.description || paramName}
            className="w-full bg-[#1e222d] border border-[#2a2e39] rounded px-3 py-2 text-slate-300 text-[11px] focus:outline-none focus:border-purple-500"
          />
        );
      
      case 'integer':
      case 'number':
        return (
          <input
            type="number"
            value={value}
            onChange={(e) => handleChange(Number(e.target.value))}
            placeholder={paramSchema.description || paramName}
            className="w-full bg-[#1e222d] border border-[#2a2e39] rounded px-3 py-2 text-slate-300 text-[11px] focus:outline-none focus:border-purple-500"
          />
        );
      
      case 'boolean':
        return (
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={Boolean(value)}
              onChange={(e) => handleChange(e.target.checked)}
              className="rounded"
            />
            <span className="text-slate-300 text-[11px]">
              {paramSchema.description || paramName}
            </span>
          </label>
        );
      
      default:
        return (
          <textarea
            value={value}
            onChange={(e) => handleChange(e.target.value)}
            placeholder={paramSchema.description || paramName}
            className="w-full bg-[#1e222d] border border-[#2a2e39] rounded px-3 py-2 text-slate-300 text-[11px] focus:outline-none focus:border-purple-500 h-20 resize-none"
          />
        );
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-[#1e222d] border border-[#2a2e39] rounded-xl w-full max-w-6xl max-h-[90vh] flex flex-col shadow-2xl">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-[#2a2e39]">
          <div>
            <h2 className="text-xl font-black text-white uppercase italic tracking-tighter">
              MCP <span className="text-purple-500">Tools</span> Interface
            </h2>
            <p className="text-[10px] text-slate-500 uppercase tracking-widest mt-1">
              Modular Capability Protocol Manager
            </p>
          </div>
          <button 
            onClick={onClose}
            className="text-slate-500 hover:text-white transition-colors"
          >
            <i className="fas fa-times text-xl"></i>
          </button>
        </div>

        {/* Main Content */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Tab Navigation */}
          <div className="border-b border-[#2a2e39] px-6">
            <div className="flex space-x-6">
              <button
                onClick={() => setActiveTab('tools')}
                className={`py-4 px-2 border-b-2 font-black text-[11px] uppercase tracking-widest transition-colors ${
                  activeTab === 'tools' 
                    ? 'border-purple-500 text-purple-500' 
                    : 'border-transparent text-slate-500 hover:text-slate-300'
                }`}
              >
                Tools ({filteredTools.length})
              </button>
              <button
                onClick={() => setActiveTab('audit')}
                className={`py-4 px-2 border-b-2 font-black text-[11px] uppercase tracking-widest transition-colors ${
                  activeTab === 'audit' 
                    ? 'border-purple-500 text-purple-500' 
                    : 'border-transparent text-slate-500 hover:text-slate-300'
                }`}
              >
                Audit Logs ({auditLogs.length})
              </button>
              <button
                onClick={() => setActiveTab('stats')}
                className={`py-4 px-2 border-b-2 font-black text-[11px] uppercase tracking-widest transition-colors ${
                  activeTab === 'stats' 
                    ? 'border-purple-500 text-purple-500' 
                    : 'border-transparent text-slate-500 hover:text-slate-300'
                }`}
              >
                Statistics
              </button>
            </div>
          </div>
          
          {/* Tab Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {activeTab === 'tools' && (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Tools List */}
                <div className="lg:col-span-1 space-y-4">
                  <div className="bg-[#131722] border border-[#2a2e39] rounded-lg p-4">
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="text-[12px] font-black text-slate-500 uppercase tracking-widest">
                        Available Tools
                      </h3>
                      <span className="text-[10px] bg-purple-500/10 text-purple-500 px-2 py-1 rounded font-black">
                        {filteredTools.length}
                      </span>
                    </div>
                    
                    {/* Search */}
                    <input
                      type="text"
                      placeholder="Search tools..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full bg-[#1e222d] border border-[#2a2e39] rounded px-3 py-2 text-slate-300 text-[11px] mb-4 focus:outline-none focus:border-purple-500"
                    />

                    {/* Tools List */}
                    <div className="space-y-2 max-h-[500px] overflow-y-auto">
                      {loading ? (
                        <div className="flex items-center justify-center py-8">
                          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-500"></div>
                        </div>
                      ) : (
                        Object.entries(groupedTools).map(([category, categoryTools]) => (
                          <div key={category} className="mb-4">
                            <h4 className="text-[10px] font-black text-slate-600 uppercase tracking-widest mb-2 px-2">
                              {category}
                            </h4>
                            {(categoryTools as McpTool[]).map(tool => (
                              <div
                                key={tool.name}
                                onClick={() => setSelectedTool(tool)}
                                className={`p-3 rounded cursor-pointer transition-all border ${
                                  selectedTool?.name === tool.name
                                    ? 'bg-purple-500/20 border-purple-500/50'
                                    : 'bg-[#1e222d] border-[#2a2e39] hover:bg-[#2a2e39]'
                                }`}
                              >
                                <div className="text-[11px] font-bold text-slate-300 mb-1">
                                  {tool.name}
                                </div>
                                <div className="text-[9px] text-slate-500 leading-tight">
                                  {tool.description}
                                </div>
                              </div>
                            ))}
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </div>

                {/* Tool Details and Execution */}
                <div className="lg:col-span-2 space-y-6">
                  {/* Tool Details */}
                  {selectedTool && (
                    <div className="bg-[#131722] border border-[#2a2e39] rounded-lg p-6">
                      <h3 className="text-[12px] font-black text-slate-500 uppercase tracking-widest mb-4">
                        Tool Configuration
                      </h3>
                      
                      <div className="mb-4">
                        <div className="text-[14px] font-bold text-white mb-1">
                          {selectedTool.name}
                        </div>
                        <div className="text-[11px] text-slate-400">
                          {selectedTool.description}
                        </div>
                        {selectedTool.category && (
                          <div className="mt-2">
                            <span className="inline-block bg-slate-700/20 text-slate-400 text-[9px] px-2 py-1 rounded uppercase font-black">
                              {selectedTool.category}
                            </span>
                          </div>
                        )}
                      </div>

                      {/* Parameter Inputs */}
                      {selectedTool.inputSchema?.properties && (
                        <div className="space-y-4">
                          <h4 className="text-[11px] font-black text-slate-500 uppercase tracking-widest">
                            Parameters
                          </h4>
                          {Object.entries(selectedTool.inputSchema.properties).map(([paramName, paramSchema]: [string, any]) => (
                            <div key={paramName}>
                              <label className="block text-[11px] text-slate-300 mb-2 font-medium">
                                {paramName}
                                {selectedTool.inputSchema.required?.includes(paramName) && (
                                  <span className="text-red-400 ml-1">*</span>
                                )}
                              </label>
                              {renderParameterInput(paramName, paramSchema)}
                              {paramSchema.description && (
                                <div className="text-[9px] text-slate-500 mt-1">
                                  {paramSchema.description}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Execute Button with Enhanced Status */}
                      <div className="mt-6 pt-6 border-t border-[#2a2e39]">
                        <div className="flex items-center justify-between mb-3">
                          <span className="text-[10px] text-slate-500 uppercase tracking-widest">
                            {isExecuting ? 'Tool Status: Executing' : 'Ready to Execute'}
                          </span>
                          <div className={`flex items-center gap-1 text-[9px] font-black ${
                            isExecuting ? 'text-yellow-400' : selectedTool ? 'text-green-400' : 'text-slate-500'
                          }`}>
                            <i className={`fas ${isExecuting ? 'fa-spinner fa-spin' : selectedTool ? 'fa-check' : 'fa-info-circle'}`}></i>
                            {isExecuting ? 'RUNNING' : selectedTool ? 'CONFIGURED' : 'SELECT TOOL'}
                          </div>
                        </div>
                        <button
                          onClick={executeTool}
                          disabled={isExecuting || !selectedTool}
                          className="w-full bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 disabled:from-slate-600 disabled:to-slate-600 text-white font-black py-3 px-4 rounded-lg transition-all uppercase text-[10px] tracking-wider shadow-lg hover:shadow-purple-500/20 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                        >
                          {isExecuting ? (
                            <>
                              <i className="fas fa-spinner fa-spin mr-2"></i>
                              Executing Tool...
                            </>
                          ) : (
                            <>
                              <i className="fas fa-bolt mr-2"></i>
                              Execute {selectedTool?.name || 'Tool'}
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Execution History */}
                  <div className="bg-[#131722] border border-[#2a2e39] rounded-lg p-6">
                    <h3 className="text-[12px] font-black text-slate-500 uppercase tracking-widest mb-4">
                      Execution History
                    </h3>
                    
                    <div className="space-y-3 max-h-[300px] overflow-y-auto">
                      {executions.length === 0 ? (
                        <div className="text-center py-8 text-slate-600">
                          <i className="fas fa-history text-2xl mb-2 opacity-20"></i>
                          <p className="text-[10px] uppercase tracking-widest">No executions yet</p>
                        </div>
                      ) : (
                        executions.map(execution => (
                          <div key={execution.id} className="bg-[#1e222d] border border-[#2a2e39] rounded p-3">
                            <div className="flex justify-between items-start mb-2">
                              <div>
                                <div className="text-[11px] font-bold text-slate-300">
                                  {execution.toolName}
                                </div>
                                <div className="text-[9px] text-slate-500">
                                  {execution.timestamp}
                                </div>
                              </div>
                              <div className={`px-2 py-1 rounded text-[9px] font-black uppercase ${
                                execution.status === 'completed' ? 'bg-green-500/20 text-green-400' :
                                execution.status === 'failed' ? 'bg-red-500/20 text-red-400' :
                                execution.status === 'running' ? 'bg-yellow-500/20 text-yellow-400 animate-pulse' :
                                'bg-slate-500/20 text-slate-400'
                              }`}>
                                {execution.status}
                              </div>
                            </div>
                            
                            {execution.parameters && Object.keys(execution.parameters).length > 0 && (
                              <div className="text-[9px] text-slate-400 mb-2">
                                Params: {JSON.stringify(execution.parameters)}
                              </div>
                            )}
                            
                            {execution.result && (
                              <div className={`text-[10px] rounded p-3 mt-2 max-h-32 overflow-y-auto ${
                                execution.status === 'failed' 
                                  ? 'bg-red-900/30 text-red-300 border border-red-500/30' 
                                  : execution.status === 'completed'
                                  ? 'bg-green-900/20 text-green-300 border border-green-500/30'
                                  : 'bg-[#0f1115] text-slate-300 border border-[#2a2e39]'
                              }`}>
                                <div className="font-mono whitespace-pre-wrap break-words">
                                  {typeof execution.result === 'string' 
                                    ? execution.result.length > 500 
                                      ? execution.result.substring(0, 500) + '...' 
                                      : execution.result
                                    : JSON.stringify(execution.result, null, 2)}
                                </div>
                                {execution.executionTime && (
                                  <div className="mt-2 text-[9px] text-slate-400 border-t border-slate-700/50 pt-2">
                                    ⚡ Execution Time: {execution.executionTime.toFixed(2)}s
                                  </div>
                                )}
                                {execution.status === 'failed' && (
                                  <div className="mt-2 flex items-center gap-1 text-red-400 pt-2 border-t border-red-500/30">
                                    <i className="fas fa-exclamation-triangle text-[9px]"></i>
                                    <span className="font-bold uppercase">EXECUTION FAILED</span>
                                  </div>
                                )}
                                {execution.status === 'completed' && (
                                  <div className="mt-2 flex items-center gap-1 text-green-400 pt-2 border-t border-green-500/30">
                                    <i className="fas fa-check-circle text-[9px]"></i>
                                    <span className="font-bold uppercase">SUCCESS</span>
                                  </div>
                                )}
                              </div>
                            )}
                            
                            {execution.error && (
                              <div className="text-[10px] text-red-400 bg-red-900/20 rounded p-2 mt-2">
                                Error: {execution.error}
                              </div>
                            )}
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            {activeTab === 'audit' && (
              <div className="space-y-6">
                <div className="bg-[#131722] border border-[#2a2e39] rounded-lg p-6">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-[12px] font-black text-slate-500 uppercase tracking-widest">
                      Recent Audit Logs
                    </h3>
                    <button 
                      onClick={loadAuditLogs}
                      className="text-[10px] bg-purple-500/10 text-purple-500 px-2 py-1 rounded font-black hover:bg-purple-500/20 transition-colors"
                    >
                      Refresh
                    </button>
                  </div>
                  
                  <div className="space-y-3 max-h-[600px] overflow-y-auto">
                    {auditLogs.length === 0 ? (
                      <div className="text-center py-8 text-slate-600">
                        <i className="fas fa-clipboard-list text-2xl mb-2 opacity-20"></i>
                        <p className="text-[10px] uppercase tracking-widest">No audit logs available</p>
                      </div>
                    ) : (
                      auditLogs.map((log, index) => (
                        <div key={index} className="bg-[#1e222d] border border-[#2a2e39] rounded p-3 text-[10px]">
                          <div className="flex justify-between items-start mb-2">
                            <div className="font-bold text-slate-300">
                              {log.tool_name} <span className="text-slate-500">on {log.server}</span>
                            </div>
                            <div className={`px-2 py-1 rounded text-[9px] font-black uppercase ${
                              log.success ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                            }`}>
                              {log.success ? 'SUCCESS' : 'FAILED'}
                            </div>
                          </div>
                          <div className="text-slate-400 mb-2">
                            User: {log.user_id} | {new Date(log.timestamp).toLocaleString()}
                          </div>
                          <div className="text-slate-500 mb-2">
                            Parameters: {JSON.stringify(log.parameters || {})}
                          </div>
                          {log.execution_time && (
                            <div className="text-slate-400 text-[9px]">
                              ⚡ {log.execution_time.toFixed(3)}s
                            </div>
                          )}
                          {log.error && (
                            <div className="text-red-400 bg-red-900/20 rounded p-2 mt-2 text-[9px]">
                              Error: {log.error}
                            </div>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>
            )}
            
            {activeTab === 'stats' && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="bg-[#131722] border border-[#2a2e39] rounded-lg p-4">
                    <div className="text-[10px] text-slate-500 uppercase tracking-widest mb-2">Total Executions</div>
                    <div className="text-2xl font-black text-white">
                      {statistics?.total_executions || 0}
                    </div>
                  </div>
                  <div className="bg-[#131722] border border-[#2a2e39] rounded-lg p-4">
                    <div className="text-[10px] text-slate-500 uppercase tracking-widest mb-2">Success Rate</div>
                    <div className="text-2xl font-black text-green-400">
                      {statistics ? `${statistics.success_rate?.toFixed(1) || 0}%` : '0%'}
                    </div>
                  </div>
                  <div className="bg-[#131722] border border-[#2a2e39] rounded-lg p-4">
                    <div className="text-[10px] text-slate-500 uppercase tracking-widest mb-2">Avg Execution Time</div>
                    <div className="text-2xl font-black text-blue-400">
                      {statistics ? `${statistics.avg_execution_time?.toFixed(2) || 0}s` : '0s'}
                    </div>
                  </div>
                  <div className="bg-[#131722] border border-[#2a2e39] rounded-lg p-4">
                    <div className="text-[10px] text-slate-500 uppercase tracking-widest mb-2">Failures</div>
                    <div className="text-2xl font-black text-red-400">
                      {statistics?.failures || 0}
                    </div>
                  </div>
                </div>
                
                {/* Server Health Status */}
                <div className="bg-[#131722] border border-[#2a2e39] rounded-lg p-6">
                  <h3 className="text-[12px] font-black text-slate-500 uppercase tracking-widest mb-4">
                    Server Health Status
                  </h3>
                  
                  {serverHealth ? (
                    <div className="space-y-3">
                      <div className={`flex items-center gap-2 text-[11px] font-bold ${
                        serverHealth.status === 'healthy' ? 'text-green-400' : 'text-yellow-400'
                      }`}>
                        <i className={`fas ${serverHealth.status === 'healthy' ? 'fa-check-circle' : 'fa-exclamation-triangle'}`}></i>
                        Overall Status: {serverHealth.status.toUpperCase()}
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {Object.entries(serverHealth.servers || {}).map(([serverName, serverInfo]: [string, any]) => (
                          <div key={serverName} className="bg-[#1e222d] border border-[#2a2e39] rounded p-3">
                            <div className="flex justify-between items-center mb-2">
                              <span className="text-[11px] font-bold text-slate-300">{serverName}</span>
                              <span className={`text-[9px] px-2 py-1 rounded font-black uppercase ${
                                serverInfo.status === 'online' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                              }`}>
                                {serverInfo.status}
                              </span>
                            </div>
                            <div className="text-[10px] text-slate-400">
                              Tools: {serverInfo.tools_count || 0}
                            </div>
                            <div className="text-[9px] text-slate-500 mt-1">
                              Last checked: {new Date(serverInfo.last_checked).toLocaleTimeString()}
                            </div>
                            {serverInfo.error && (
                              <div className="text-[9px] text-red-400 mt-2">
                                Error: {serverInfo.error}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-8 text-slate-600">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-500 mx-auto mb-2"></div>
                      <p className="text-[10px] uppercase tracking-widest">Loading server health...</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-[#2a2e39] bg-[#1a1e27] rounded-b-xl">
          <div className="flex justify-between items-center text-[10px] text-slate-500 uppercase tracking-widest">
            <div className="flex items-center gap-4">
              <span>Total Tools: {tools.length}</span>
              <span>Executions: {executions.length}</span>
              {activeTab === 'stats' && statistics && (
                <span>Success Rate: {statistics.success_rate?.toFixed(1) || 0}%</span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <i className={`fas fa-circle ${loading ? 'text-yellow-500' : 'text-green-500'}`}></i>
              <span>{loading ? 'Loading...' : activeTab === 'tools' ? 'Ready' : activeTab === 'audit' ? 'Monitoring' : 'Analyzing'}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default McpToolsPanel;